export const CAMPAIGN_LEVEL_QUERY_PARAM_TEMPLATE_GATE =
  "bazaar.tapestry.campaign_level_query_param_template";
export const OPPREF_LANDING_PAGE_QUERY_TEMPLATE_GATE = "bazaar.oppref_landing_page_query_template";
export const PRODUCT_FEED_CAMPAIGN_MODE = "product_feed";
export const BUSINESS_AGENT_CAMPAIGN_MODE = "business_agent";

export function canUseCampaignLevelQueryParamTemplate({
  isCampaignLevelQueryParamTemplateEnabled,
  campaignMode,
}: {
  isCampaignLevelQueryParamTemplateEnabled: boolean;
  campaignMode?: string | null;
}): boolean {
  if (campaignMode === BUSINESS_AGENT_CAMPAIGN_MODE) {
    return false;
  }
  return campaignMode === PRODUCT_FEED_CAMPAIGN_MODE || isCampaignLevelQueryParamTemplateEnabled;
}

export const SUPPORTED_LANDING_PAGE_QUERY_TEMPLATE_VALUES = [
  "campaign_id",
  "ad_group_id",
  "ad_id",
  "ad_account_id",
] as const;

export function getSupportedLandingPageQueryTemplateValues(
  isOpprefMacroEnabled = false,
): readonly string[] {
  return isOpprefMacroEnabled ?
      [...SUPPORTED_LANDING_PAGE_QUERY_TEMPLATE_VALUES, "oppref"]
    : SUPPORTED_LANDING_PAGE_QUERY_TEMPLATE_VALUES;
}

const SUPPORTED_TEMPLATE_VALUE_LABELS = SUPPORTED_LANDING_PAGE_QUERY_TEMPLATE_VALUES.map(
  (value) => `{${value}}`,
);
export const RESERVED_LANDING_PAGE_QUERY_PARAM_NAMES = ["olref", "oppref"];
const RESERVED_LANDING_PAGE_QUERY_PARAM_NAME_SET = new Set(RESERVED_LANDING_PAGE_QUERY_PARAM_NAMES);
const TEMPLATE_VALUE_REGEX = /\{([^{}]*)\}/g;

export const LANDING_PAGE_QUERY_PARAMETERS_HINT = `Optional. Appends missing query parameters. Supported template values: ${SUPPORTED_TEMPLATE_VALUE_LABELS.join(
  ", ",
)}.`;

export const LANDING_PAGE_QUERY_PARAMETERS_FORMAT_ERROR =
  "Enter query parameters in key=value format.";

export const LANDING_PAGE_QUERY_PARAMETERS_UNSUPPORTED_TEMPLATE_VALUE_ERROR = `Supported template values: ${SUPPORTED_TEMPLATE_VALUE_LABELS.join(", ")}.`;

export const LANDING_PAGE_QUERY_PARAMETERS_TEMPLATE_NAME_ERROR =
  "Template values are only supported in query parameter values.";

export const LANDING_PAGE_QUERY_PARAMETERS_RESERVED_NAME_ERROR = `Reserved query parameters are not supported: ${RESERVED_LANDING_PAGE_QUERY_PARAM_NAMES.join(", ")}.`;

export function getLandingPageQueryStringTemplateValidationError(
  queryStringTemplate: string,
  { isOpprefMacroEnabled = false }: { isOpprefMacroEnabled?: boolean } = {},
): string | undefined {
  const trimmedTemplate = queryStringTemplate.trim();
  if (!trimmedTemplate) {
    return undefined;
  }

  const queryString = trimmedTemplate.startsWith("?") ? trimmedTemplate.slice(1) : trimmedTemplate;
  if (!queryString.trim()) {
    return LANDING_PAGE_QUERY_PARAMETERS_FORMAT_ERROR;
  }

  const queryParameters = queryString.split("&");
  if (
    queryParameters.some(
      (queryParameter) => !queryParameter.trim() || !queryParameter.includes("="),
    )
  ) {
    return LANDING_PAGE_QUERY_PARAMETERS_FORMAT_ERROR;
  }

  const hasEmptyQueryParameterName = queryParameters.some((queryParameter) => {
    const separatorIndex = queryParameter.indexOf("=");
    return queryParameter.slice(0, separatorIndex).trim().length === 0;
  });
  if (hasEmptyQueryParameterName) {
    return LANDING_PAGE_QUERY_PARAMETERS_FORMAT_ERROR;
  }

  const supportedTemplateValues = new Set(
    getSupportedLandingPageQueryTemplateValues(isOpprefMacroEnabled),
  );
  for (const queryParameter of queryParameters) {
    const separatorIndex = queryParameter.indexOf("=");
    const queryParameterName = queryParameter.slice(0, separatorIndex).trim();
    const queryParameterValue = queryParameter.slice(separatorIndex + 1);

    if (queryParameterName.match(TEMPLATE_VALUE_REGEX) != null) {
      return LANDING_PAGE_QUERY_PARAMETERS_TEMPLATE_NAME_ERROR;
    }

    if (queryParameterName.match(/[{}]/) != null) {
      return LANDING_PAGE_QUERY_PARAMETERS_UNSUPPORTED_TEMPLATE_VALUE_ERROR;
    }

    if (RESERVED_LANDING_PAGE_QUERY_PARAM_NAME_SET.has(queryParameterName.toLowerCase())) {
      return LANDING_PAGE_QUERY_PARAMETERS_RESERVED_NAME_ERROR;
    }

    const templateValueMatches = queryParameterValue.match(TEMPLATE_VALUE_REGEX) ?? [];
    for (const templateValueMatch of templateValueMatches) {
      const templateValue = templateValueMatch.slice(1, -1);
      if (!supportedTemplateValues.has(templateValue)) {
        return LANDING_PAGE_QUERY_PARAMETERS_UNSUPPORTED_TEMPLATE_VALUE_ERROR;
      }
    }

    if (queryParameterValue.replace(TEMPLATE_VALUE_REGEX, "").match(/[{}]/) != null) {
      return LANDING_PAGE_QUERY_PARAMETERS_UNSUPPORTED_TEMPLATE_VALUE_ERROR;
    }
  }

  return undefined;
}
